import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getInspectionStatusList } from '../../../../helpers/inspection_status/api_helper';
import { formatDate, toStr, sanitizeStrArr, toSelectOpts } from '../utils';

// Module-level selectors — created once, not re-created on every render
const selectorProfile = createSelector(
	state => state.Profile,
	profile => profile.user
);

const selectorInspection = createSelector(
	state => state.Inspection,
	data => ({ isAdding: data.isAdding, isUpdating: data.isUpdating })
);

const normalizeStr = s => (s || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();

const useFiscalitationForm = ({
	dataFiscalizacion,
	isOpen,
	handleSave,
	establishmentOptions,
	inspectionTypeOptions,
	inspectionAuthorityOptions,
	personOptions,
	managementPositionOptions,
	fiscalizerOptions,
}) => {
	const profileUser = useSelector(selectorProfile);
	const { isAdding, isUpdating } = useSelector(selectorInspection);
	const canCerrar = profileUser?.role_code === 'MAB' || profileUser?.role_code === 'ADM';

	const [selectedFile, setSelectedFile] = useState(null);
	const [selectedClosureFile, setSelectedClosureFile] = useState(null);
	const [enEspera, setEnEspera] = useState(false);
	const [createdPersons, setCreatedPersons] = useState([]);
	const [createdPositions, setCreatedPositions] = useState([]);
	const [createdFiscalizadores, setCreatedFiscalizadores] = useState([]);

	const submitModeRef = useRef('save');
	const inspectionStatusesRef = useRef([]);
	const submittingRef = useRef(false);

	useEffect(() => {
		getInspectionStatusList()
			.then(data => {
				inspectionStatusesRef.current = Array.isArray(data) ? data : (data?.data || []);
			})
			.catch(() => { inspectionStatusesRef.current = []; });
	}, []);

	useEffect(() => {
		if (isOpen) {
			setSelectedFile(null);
			setSelectedClosureFile(null);
			setCreatedPersons([]);
			setCreatedPositions([]);
			setCreatedFiscalizadores([]);
			setEnEspera(!!dataFiscalizacion?.waiting_for_observations || !!dataFiscalizacion?.review_notes);
		}
	}, [isOpen]); // eslint-disable-line

	//Option normalization

	const stablePersonOptions = useMemo(() => toSelectOpts(personOptions), [personOptions]);
	const stableMgmtPositionOptions = useMemo(
		() => toSelectOpts(managementPositionOptions),
		[managementPositionOptions]
	);
	const stableFiscalizerOptions = useMemo(
		() => Array.isArray(fiscalizerOptions) ? fiscalizerOptions : [],
		[fiscalizerOptions]
	);

	const estSelectData = useMemo(
		() => toSelectOpts(establishmentOptions).map(o => ({ value: toStr(o.value), label: toStr(o.label) })),
		[establishmentOptions]
	);
	const typeSelectData = useMemo(
		() => toSelectOpts(inspectionTypeOptions).map(o => ({ value: toStr(o.value), label: toStr(o.label) })),
		[inspectionTypeOptions]
	);
	const authoritySelectData = useMemo(
		() => toSelectOpts(inspectionAuthorityOptions).map(opt => ({
			label: toStr(opt.label ?? opt.value).trim(),
			value: toStr(opt.value).trim(),
		})),
		[inspectionAuthorityOptions]
	);
	const personSelectData = useMemo(
		() => [
			...stablePersonOptions.map(p => ({ value: toStr(p.value), label: toStr(p.label) })),
			...createdPersons,
		].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
		[stablePersonOptions, createdPersons]
	);
	const positionSelectData = useMemo(
		() => [
			...stableMgmtPositionOptions.map(p => ({ value: toStr(p.value), label: toStr(p.label) })),
			...createdPositions,
		].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
		[stableMgmtPositionOptions, createdPositions]
	);
	const fiscalizadorSelectData = useMemo(
		() => [
			...stableFiscalizerOptions.map(o => ({ value: toStr(o.value), label: toStr(o.label) })),
			...createdFiscalizadores,
		].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
		[stableFiscalizerOptions, createdFiscalizadores]
	);

	//Formik

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			fecha: formatDate(dataFiscalizacion?.fecha) ?? null,
			establecimiento_id: toStr(dataFiscalizacion?.establecimiento_id),
			tipo: toStr(dataFiscalizacion?.tipo),
			entidades: sanitizeStrArr(dataFiscalizacion?.entidades),
			responsable_id: toStr(dataFiscalizacion?.responsable_id),
			management_position_id: toStr(dataFiscalizacion?.management_position_id),
			fiscalizadores: sanitizeStrArr(dataFiscalizacion?.fiscalizadores),
			observaciones: dataFiscalizacion?.observaciones || '',
			review_notes: dataFiscalizacion?.review_notes || '',
		},
		validationSchema: Yup.object({
			fecha: Yup.date().nullable().required('Campo requerido'),
			establecimiento_id: Yup.string().required('Campo requerido'),
			tipo: Yup.string().required('Campo requerido'),
			entidades: Yup.array().min(1, 'Seleccione al menos una entidad').required('Campo requerido'),
			responsable_id: Yup.string().required('Campo requerido'),
			fiscalizadores: Yup.array().min(1, 'Agregue al menos un fiscalizador').required('Campo requerido'),
			observaciones: Yup.string(),
		}),
		onSubmit: (values, { resetForm }) => {
			const isCerrar = submitModeRef.current === 'close';
			const cerradoStatus = inspectionStatusesRef.current.find(
				s => normalizeStr(s.name) === 'cerrado'
			);
			const enRevisionStatus = inspectionStatusesRef.current.find(
				s => normalizeStr(s.name).includes('revision')
			);
			let resolvedStatusId;
			if (isCerrar && cerradoStatus?._id) {
				resolvedStatusId = cerradoStatus._id;
			} else if (!isCerrar && enEspera && enRevisionStatus?._id) {
				resolvedStatusId = enRevisionStatus._id;
			}
			const d = values.fecha;
			handleSave({
				...values,
				fecha: d instanceof Date
					? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
					: d ?? null,
				documento: selectedFile,
				documento_id: dataFiscalizacion?.documento_id ?? dataFiscalizacion?.file_id ?? null,			finding_closure_document: selectedClosureFile,
			finding_closure_file_id: dataFiscalizacion?.finding_closure_file_id ?? null,				waiting_for_observations: enEspera,
				...(resolvedStatusId ? { inspection_status_id: resolvedStatusId } : {}),
			});
			submitModeRef.current = 'save';
			resetForm();
			setSelectedFile(null);
		},
	});

	// Derived state (must be after useFormik)
	const isNewPerson = useMemo(
		() => !!(validation.values.responsable_id &&
			!stablePersonOptions.some(p => toStr(p.value) === validation.values.responsable_id)),
		[stablePersonOptions, validation.values.responsable_id]
	);

	const cargoDisplayName = useMemo(() => {
		if (!isNewPerson && validation.values.management_position_id) {
			const pos = stableMgmtPositionOptions.find(
				p => toStr(p.value) === validation.values.management_position_id
			);
			return pos?.label || validation.values.management_position_id;
		}
		return '';
	}, [isNewPerson, stableMgmtPositionOptions, validation.values.management_position_id]);

	// Auto-sync management_position_id when the selected responsable changes
	useEffect(() => {
		if (!validation.values.responsable_id) {
			if (validation.values.management_position_id) {
				validation.setFieldValue('management_position_id', '');
			}
			return;
		}
		const person = stablePersonOptions.find(
			p => toStr(p.value) === validation.values.responsable_id
		);
		if (person) {
			const posId = toStr(person.item?.management_position_id || '');
			if (posId !== validation.values.management_position_id) {
				validation.setFieldValue('management_position_id', posId);
			}
		} else {
			if (validation.values.management_position_id) {
				validation.setFieldValue('management_position_id', '');
			}
		}
	}, [validation.values.responsable_id]); // eslint-disable-line

	useEffect(() => {
		if (!isAdding && !isUpdating && !validation.isSubmitting) {
			submittingRef.current = false;
		}
	}, [isAdding, isUpdating, validation.isSubmitting]); // eslint-disable-line

	return {
		isAdding,
		isUpdating,
		canCerrar,
		validation,
		selectedFile,
		setSelectedFile,
		selectedClosureFile,
		setSelectedClosureFile,
		enEspera,
		setEnEspera,
		submitModeRef,
		submittingRef,
		estSelectData,
		typeSelectData,
		authoritySelectData,
		personSelectData,
		positionSelectData,
		fiscalizadorSelectData,
		isNewPerson,
		cargoDisplayName,
		setCreatedPersons,
		setCreatedPositions,
		setCreatedFiscalizadores,
	};
};

export default useFiscalitationForm;
